/**
 * entry-prompts.tsx — Điều phối các lời mời hiện khi vào web, MỘT cái một lúc (gắn trong AppLayout, không vẽ gì khi không có):
 *   1. Hộp xin quyền thông báo (nếu trình duyệt hỗ trợ và chưa hỏi / chưa hoãn);
 *   2. rồi thẻ nổi mời cài ứng dụng ở góc (nếu cài được và chưa cài / chưa hoãn);
 *   3. rồi thẻ nổi nhắc chủ sở hữu bật xác thực 2 lớp nếu chưa bật (không ép — "Để sau" hoãn 7 ngày, tự hiện
 *      lại nếu tới lúc đó vẫn chưa bật). Owner KHÔNG còn bị ép thiết lập TOTP ngay lúc đăng nhập (30/10/2026) —
 *      đây là thay thế: nhắc nhẹ sau khi đã vào app, không chặn đường vào.
 * Chưa hiện khi: popup chào mừng sau đăng ký còn mở, phiên "xem dưới quyền", đang ở màn chat điện thoại (đè ô soạn tin),
 * thanh nổi "Tiếp tục thiết lập (x/2)" của Bắt đầu đang hiện (cùng góc dưới-phải trên máy tính — `onboarding-entry.tsx`,
 * chung đối tượng chủ sở hữu mới nên rất dễ đụng độ với thẻ cài app / thẻ nhắc TOTP nếu không tránh), hoặc chưa qua vài
 * giây đầu (cho trang tải xong, tránh bật ra trước mặt người dùng).
 */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { useTotpStatus } from '@/hooks/use-settings'
import { isWelcomePending } from '@/lib/welcome'
import { isChatDetailPath } from '@/lib/mobile-nav'
import { isSnoozed, snooze } from '@/lib/pwa-install'
import { isResumeClosed, resumeBarVisible } from '@/lib/onboarding'
import { useOnboardingEntry } from '@/components/shared/onboarding-entry'
import { desktopPermission } from '@/components/shared/notification-center'
import { InstallPromoCard, useInstallApp } from '@/components/shared/install-app'
import { NotifyPermissionDialog } from '@/components/shared/notify-permission-dialog'
import { TotpNudgeCard } from '@/components/shared/totp-nudge'

const FIRST_DELAY_MS = 4000
const BETWEEN_DELAY_MS = 1200

export function EntryPrompts({ mobile }: { mobile: boolean }) {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id
  const isOwner = user?.role === 'owner'
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const install = useInstallApp()
  const totpStatus = useTotpStatus(isOwner)
  const onboarding = useOnboardingEntry()
  const [ready, setReady] = useState(false)
  const [gapOver, setGapOver] = useState(false)
  const [gapOver2, setGapOver2] = useState(false)
  const [notifyDone, setNotifyDone] = useState(false)
  const [installDone, setInstallDone] = useState(false)
  const [totpDone, setTotpDone] = useState(false)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [onboardingBarClosed, setOnboardingBarClosed] = useState(() => isResumeClosed(userId))

  useEffect(() => {
    const t = setTimeout(() => setReady(true), FIRST_DELAY_MS)
    return () => clearTimeout(t)
  }, [])

  // Popup chào mừng tự xoá dấu "chờ chào" khi đóng → hỏi lại mỗi giây tới khi hết (rẻ: chỉ chạy khi còn dấu).
  useEffect(() => {
    const pending = isWelcomePending(userId)
    setWelcomeOpen(pending)
    if (!pending) return
    const t = setInterval(() => {
      if (!isWelcomePending(userId)) { setWelcomeOpen(false); clearInterval(t) }
    }, 1000)
    return () => clearInterval(t)
  }, [userId])

  useEffect(() => setOnboardingBarClosed(isResumeClosed(userId)), [userId])

  // Thanh "Tiếp tục thiết lập" chỉ nổi trên máy tính (ẩn hẳn ở điện thoại — onboarding-entry.tsx).
  const onboardingBarUp = !mobile && resumeBarVisible({
    pathname, status: onboarding.status, user: onboarding.user, closedThisSession: onboardingBarClosed,
  })

  // Nút X của thanh đó không phát sự kiện ra ngoài (chỉ set 1 biến module-level riêng) → dò lại mỗi giây
  // tới khi đóng hẳn (rẻ: chỉ chạy lúc thanh còn đang chặn), giống cách popup chào mừng đang làm ở trên.
  useEffect(() => {
    if (!onboardingBarUp) return
    const t = setInterval(() => setOnboardingBarClosed(isResumeClosed(userId)), 1000)
    return () => clearInterval(t)
  }, [onboardingBarUp, userId])

  const allowed = !!user && !user.impersonatedBy && !user.platformActorId && !welcomeOpen && !onboardingBarUp
    && !(mobile && isChatDetailPath(pathname))
  const notifyDue = !notifyDone && desktopPermission() === 'default' && !isSnoozed('notify')
  const installDue = !installDone && install.canOffer && !isSnoozed('install')
  const totpDue = !totpDone && isOwner && totpStatus.data?.enabled === false && !isSnoozed('totp')

  // Giữa hộp thông báo và thẻ cài đặt chừa một nhịp để hai thứ không dồn vào nhau.
  const notifyGone = !notifyDue
  useEffect(() => {
    if (!ready || !notifyGone) return
    const t = setTimeout(() => setGapOver(true), BETWEEN_DELAY_MS)
    return () => clearTimeout(t)
  }, [ready, notifyGone])

  // Rồi giữa thẻ cài đặt và thẻ nhắc TOTP cũng chừa một nhịp tương tự.
  const installGone = gapOver && !installDue
  useEffect(() => {
    if (!installGone) return
    const t = setTimeout(() => setGapOver2(true), BETWEEN_DELAY_MS)
    return () => clearTimeout(t)
  }, [installGone])

  if (!allowed || !ready) return null
  if (notifyDue) return <NotifyPermissionDialog onDone={() => setNotifyDone(true)} />
  if (installDue && gapOver) {
    const close = () => { snooze('install'); setInstallDone(true) }
    return <InstallPromoCard mobile={mobile} onClose={close} onInstall={() => { close(); void install.run() }} />
  }
  if (totpDue && gapOver2) {
    return (
      <TotpNudgeCard
        mobile={mobile}
        onClose={() => { snooze('totp'); setTotpDone(true) }}
        onEnable={() => { setTotpDone(true); navigate('/account/profile') }}
      />
    )
  }
  return null
}
